import React from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { InformationCircleIcon as WarningIcon } from '@heroicons/react/24/solid';
import { FormattedMessage, FormattedNumber, MessageDescriptor } from 'react-intl';

import PaymentStatus from 'modules/onlinePayment/types/PaymentStatus';
import { FinalInvoice } from 'modules/selfServices/types/SelfService';

import { b } from 'utils/i18nUtils';
import { roundAmountByCountry } from 'utils/priceUtils';
import { useProceedToPaymentMutation } from 'modules/selfServices/service';

import { closePayingModal, openPayingModal } from 'modules/onlinePayment/actions';

import { isPublicDevice } from 'modules/dealers/selectors';
import { isInstructionModalDisplayed } from 'modules/sockets/selectors';
import { getCountry, isInvoicePaymentDelayable } from 'modules/selfServices/selectors';
import { getStatus, isPayingModalOpen as isPayingModalOpenSelector } from 'modules/onlinePayment/selectors';

import Icon from 'components/ui/Icon';
import Attachments from 'components/ui/Attachments';
import Button, { ButtonType } from 'components/ui/Button';

import PayingModal from './Modal/PayingModal';
import PendingModal from './Modal/PendingModal';
import KioskInstructionModal from './Modal/KioskInstructionModal';

interface FinalInvoiceCardProps {
  onSkip?: () => void;
  selfServiceId: string;
  currencyIsoCode: string;
  finalInvoice: FinalInvoice;
}

const FinalInvoiceCard: React.FC<FinalInvoiceCardProps> = ({
  onSkip,
  finalInvoice,
  selfServiceId,
  currencyIsoCode,
}) => {
  const dispatch = useDispatch();

  const [proceedToPayment, { isLoading: isLoadingPayment }] = useProceedToPaymentMutation();

  const country = useSelector(getCountry);
  const isPublic = useSelector(isPublicDevice);
  const paymentStatus = useSelector(getStatus);
  const canDelayPayment = useSelector(isInvoicePaymentDelayable);
  const isPayingModalOpen = useSelector(isPayingModalOpenSelector);
  const isInstructionOpen = useSelector(isInstructionModalDisplayed);

  const [isSkipping, setIsSkipping] = React.useState(false);
  const [isPendingOpen, setPendingOpen] = React.useState(false);
  const [isRedirecting, setIsRedirecting] = React.useState(false);

  const isLoading = isLoadingPayment || isRedirecting;
  const isPaid = paymentStatus === PaymentStatus.SUCCESS;
  const isPending = paymentStatus === PaymentStatus.PENDING;
  const isDisabled = isLoading || isSkipping || isRedirecting;
  const canPay = finalInvoice.payable && !isPaid;

  const price = React.useMemo(() => roundAmountByCountry[country?.code](finalInvoice.price), [country?.code, finalInvoice.price]);

  const handleCancel = React.useCallback(() => setPendingOpen(false), []);
  const handleClosePayingModal = React.useCallback(() => dispatch(closePayingModal()), [dispatch]);

  const handlePaymentOnClick = React.useCallback(() => {
    if (finalInvoice.link) {
      setIsRedirecting(true);
      setPendingOpen(true);
      window.location.assign(finalInvoice.link);
    } else {
      proceedToPayment({ id: selfServiceId });
      setPendingOpen(false);
    }
  }, [finalInvoice.link, proceedToPayment, selfServiceId]);

  const handlePay = React.useCallback(() => {
    if (isPublic) {
      // Online payment
      if (finalInvoice.link) {
        dispatch(openPayingModal());
      } else { // Terminal payment
        proceedToPayment({ id: selfServiceId });
      }
    } else {
      // Bezahl
      // eslint-disable-next-line no-lonely-if
      if (isPending) {
        setPendingOpen(true);
      } else {
        handlePaymentOnClick();
      }
    }
  }, [dispatch, finalInvoice.link, isPending, isPublic, proceedToPayment, selfServiceId, handlePaymentOnClick]);

  const handleOnSkip = React.useCallback(() => {
    if (onSkip) {
      setIsSkipping(true);
      onSkip();
    }
  }, [onSkip]);

  const paymentButtonText: MessageDescriptor = React.useMemo(() => (
    canDelayPayment
      ? { id: 'page.finalInvoice.buttons.paymentNow', defaultMessage: 'Pay now' }
      : { id: 'page.finalInvoice.modal.kioskInstruction.proceedTitle', defaultMessage: 'Proceed with payment' }
  ), [canDelayPayment]);

  const amountLabel = React.useMemo(() => {
    if (isPaid) {
      return { id: 'page.finalInvoice.amountPaid', defaultMessage: 'Amount paid (VAT Incl.)' };
    }

    return finalInvoice.payable
      ? { id: 'page.finalInvoice.amountToPaid', defaultMessage: 'Amount to be paid (VAT incl.)' }
      : { id: 'page.finalInvoice.invoiceAmount', defaultMessage: 'Invoice amount (VAT incl.)' };
  }, [isPaid, finalInvoice.payable]);

  const downloadButtonProps = React.useMemo(() => ({
    loading: isLoading,
    disabled: isPendingOpen || isInstructionOpen,
  }), [isLoading, isInstructionOpen, isPendingOpen]);

  return (
    <div
      className="rounded-card w-full border bg-card-bg
               border-border-default px-5 py-10 kioskSharebox:px-7 kioskSharebox:py-14 kiosk:px-10 kiosk:py-20"
    >
      <div className="flex items-center w-full flex-col">
        <span
          className={classNames('bg-white rounded-full p-5 kiosk:p-10 kioskSharebox:p-7 flex items-center justify-center', {
            '!bg-success-lowest': isPaid,
          })}
        >
          {isPaid && (
            <div className="size-14 rounded-full kiosk:size-28 bg-success-default grid place-items-center">
              <Icon className="fb-icon--check text-2xl kiosk:text-6xl text-white" />
            </div>
          )}
          {!isPaid && (
            <Icon className="fb-icon--cash-banknote-invoice text-2xl kiosk:text-6xl text-default" />
          )}
        </span>

        <div className="mt-4 kioskSharebox:mt-8 kiosk:mt-12 font-medium" data-testid="status">
          <FormattedMessage {...amountLabel} tagName="p" />
        </div>

        <div className="mt-3 font-extrabold text-4xl kioskSharebox:mt-4 kiosk:mt-5 kiosk:font-normal kiosk:text-5xl" data-testid="price">
          <FormattedNumber value={price} style="currency" currency={currencyIsoCode} />
        </div>
      </div>

      <div className="mt-6 kioskSharebox:mt-8 kiosk:mt-12">
        {canPay && (
          <>
            <Button
              analytics={{
                category: 'Payment',
                action: 'Payment started',
              }}
              type={ButtonType.TERTIARY}
              className="w-full py-3 kiosk:!py-6 !text-button-xs"
              loading={isLoading}
              onClick={handlePay}
              testId="pay-button"
              disabled={isDisabled}
            >
              <FormattedMessage {...paymentButtonText} />
            </Button>
            {canDelayPayment && (
              <Button
                analytics={{
                  category: 'Payment',
                  action: 'Payment delayed',
                }}
                type={ButtonType.PRIMARY}
                className="w-full mt-3 py-3 kiosk:mt-6 kiosk:!py-6 !text-button-xs"
                loading={isLoading || isSkipping}
                onClick={handleOnSkip}
                testId="pay-later-button"
                disabled={isDisabled}
              >
                <FormattedMessage id="page.finalInvoice.buttons.paymentLater" defaultMessage="Pay at the dealership" />
              </Button>
            )}
            {isPending && (
              <div
                className="warning-alert mt-3 kiosk:mt-6 kiosk:text-xl"
                data-testid="pending-payment-warning"
              >
                <div className="mx-2 kiosk:mx-4">
                  <WarningIcon className="size-7 kiosk:size-10 text-warning-default" />
                </div>
                <span className="kiosk:!text-2xl ">
                  <FormattedMessage
                    id="page.finalInvoice.modal.pending.content"
                    defaultMessage="A payment is <b>waiting for validation</b>"
                    values={{ b: (text) => b(text, 'kiosk:text-2xl kioskSharebox:text-xl') }}
                  />
                </span>
              </div>
            )}
          </>
        )}
      </div>

      <div className="border-t my-6 kiosk:my-12 kioskSharebox:my-8 border-border-default" />

      <div className="my-6 kiosk:my-8 flex justify-between px-4 text-base text-low">
        <span className="mr-4">
          <FormattedMessage id="page.finalInvoice.invoiceNumber" defaultMessage="Invoice N°" />
        </span>
        <span className="font-bold text-default break-word" data-testid="reference">
          {finalInvoice.name}
        </span>
      </div>
      <div className="kiosk:mx-4">
        <Attachments
          documents={finalInvoice.medias}
          documentClassName={classNames({ 'first:border-0 first:pt-0 last:pb-0': !isPaid })}
          singleDownloadTitle={
            <FormattedMessage id="page.finalInvoice.buttons.download" defaultMessage="Download invoice" />
          }
          buttonProps={downloadButtonProps}
        />
      </div>

      <PendingModal
        loading={isLoading}
        open={isPendingOpen}
        onCancel={handleCancel}
        onClick={handlePaymentOnClick}
        buttonText={paymentButtonText}
      />

      <PayingModal
        open={isPayingModalOpen}
        link={finalInvoice.link}
        selfServiceId={selfServiceId}
        paymentStatus={paymentStatus}
        onCancel={handleClosePayingModal}
      />

      <KioskInstructionModal open={isInstructionOpen} isPaid={isPaid} />
    </div>
  );
};

export default FinalInvoiceCard;
